import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, FileText } from 'lucide-react';
import { clientsAPI, jobsAPI } from "../../services/api";
import { toast } from "react-toastify";

const CreateJobModal = ({ onClose, onCreate, initialClientId }) => {
  const [formData, setFormData] = useState({
    jobCode: '',
    client: '',
    jobTitle: '',
    department: '',
    jobStatus: 'OPEN',
    jobPriority: 'MEDIUM',
    jobLocation: [],
    workLocation: '',
    workModel: 'HYBRID',
    workingHours: '',
    jobType: 'FULL_TIME',
    numberOfOpenings: '1',
    primarySkills: [],
    secondarySkills: [],
    experienceMin: '',
    experienceMax: '',
    jobDescription: '',
    hiringManagerName: '',
    hiringManagerEmail: '',
    compensationCurrency: 'USD',
    compensationPaytype: '',
    compensationMin: '',
    compensationMax: '',
    education: '',
    preferredInstitution: '',
    noticePeriod: '',
    interviewMode: '',
    interviewRounds: [],
    assignedTo: '',
    vendorCommercials: '',
    workAuthorization: '',
    visaSponsorship: '',
    backgroundCheck: '',
    securityClearance: '',
    licenseCertifications: '',
    idealCandidateProfile: '',
    screeningQuestions: []
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [newJobLocation, setNewJobLocation] = useState('');
  const [newPrimarySkill, setNewPrimarySkill] = useState('');
  const [newSecondarySkill, setNewSecondarySkill] = useState('');
  const [newInterviewRound, setNewInterviewRound] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  const jobStatusOptions = ['OPEN', 'CLOSED', 'ON_HOLD', 'DRAFT'];
  const priorityOptions = ['HIGH', 'MEDIUM', 'LOW'];
  const workModelOptions = ['REMOTE', 'ONSITE', 'HYBRID'];
  const jobTypeOptions = ['FULL_TIME', 'PART_TIME', 'CONTRACT', 'TEMPORARY', 'INTERNSHIP'];
  const currencyOptions = ['USD', 'EUR', 'GBP', 'INR', 'AUD', 'CAD'];
  const compensationPaytypeOptions = ['ANNUAL', 'HOURLY', 'MONTHLY', 'PROJECT_BASED'];
  const interviewModeOptions = ['IN_PERSON', 'VIRTUAL', 'PHONE', 'ASSESSMENT'];
  const workAuthorizationOptions = ['CITIZEN', 'GREEN_CARD', 'WORK_VISA', 'TN_VISA', 'OPT_CPT', 'NO_AUTHORIZATION'];
  const visaSponsorshipOptions = ['YES', 'NO', 'DEPENDS'];
  const backgroundCheckOptions = ['YES', 'NO', 'DEPENDS'];
  const securityClearanceOptions = ['NONE', 'BASIC', 'SECRET', 'TOP_SECRET'];
  const educationOptions = ['HIGH_SCHOOL', 'ASSOCIATE', 'BACHELORS', 'MASTERS', 'PHD', 'DIPLOMA', 'CERTIFICATION'];
  const noticePeriodOptions = ['IMMEDIATE', '15_DAYS', '30_DAYS', '45_DAYS', '60_DAYS', '90_DAYS', 'MORE_THAN_90_DAYS'];

  const primaryBg = { backgroundColor: "#1C4E6D" };
  const primaryBgHover = { backgroundColor: "#153a4f" };
  const primaryColor = { color: "#1C4E6D" };
  const primaryLightBg = { backgroundColor: "#e8f4f9" };

  useEffect(() => {
    const getIds = () => {
      try {
        const profile = JSON.parse(localStorage.getItem("userProfile"));
        const orgId = profile?.organizationId || profile?.OrganizationId || localStorage.getItem("organizationId");
        if (!orgId) {
          toast.error("Organization ID not found");
          onClose();
          return;
        }
        setOrganizationId(orgId);
      } catch (e) {
        console.error("Error getting IDs:", e);
      }
    };
    getIds();
  }, []);

  useEffect(() => {
    const fetchClients = async () => {
      if (!organizationId) return;
      setLoadingClients(true);
      try {
        const response = await clientsAPI.getAllClients({ organizationId });
        setClients(response.data || []);
      } catch (error) {
        console.error("Error fetching clients:", error);
        toast.error("Failed to load clients");
      } finally {
        setLoadingClients(false);
      }
    };
    fetchClients();
  }, [organizationId]);

  useEffect(() => {
    if (initialClientId && clients.length > 0) {
      const selectedClient = clients.find(c => c.id === initialClientId);
      if (selectedClient) {
        setClientId(initialClientId);
        setFormData(prev => ({ ...prev, client: selectedClient.companyName || '' }));
      }
    }
  }, [initialClientId, clients]);

  const handleClientChange = (e) => {
    const selectedClientId = e.target.value;
    const selectedClient = clients.find(c => c.id === selectedClientId);
    setClientId(selectedClientId);
    setFormData(prev => ({ ...prev, client: selectedClient?.companyName || '' }));
    if (errors.client) setErrors(prev => ({ ...prev, client: '' }));
  };

  const addJobLocation = () => {
    if (newJobLocation.trim() && !formData.jobLocation.includes(newJobLocation.trim())) {
      setFormData(prev => ({ ...prev, jobLocation: [...prev.jobLocation, newJobLocation.trim()] }));
      setNewJobLocation('');
    }
  };

  const removeJobLocation = (index) => {
    setFormData(prev => ({ ...prev, jobLocation: prev.jobLocation.filter((_, i) => i !== index) }));
  };

  const addPrimarySkill = () => {
    if (newPrimarySkill.trim() && !formData.primarySkills.includes(newPrimarySkill.trim())) {
      setFormData(prev => ({ ...prev, primarySkills: [...prev.primarySkills, newPrimarySkill.trim()] }));
      setNewPrimarySkill('');
    }
  };

  const removePrimarySkill = (index) => {
    setFormData(prev => ({ ...prev, primarySkills: prev.primarySkills.filter((_, i) => i !== index) }));
  };

  const addSecondarySkill = () => {
    if (newSecondarySkill.trim() && !formData.secondarySkills.includes(newSecondarySkill.trim())) {
      setFormData(prev => ({ ...prev, secondarySkills: [...prev.secondarySkills, newSecondarySkill.trim()] }));
      setNewSecondarySkill('');
    }
  };

  const removeSecondarySkill = (index) => {
    setFormData(prev => ({ ...prev, secondarySkills: prev.secondarySkills.filter((_, i) => i !== index) }));
  };

  const addInterviewRound = () => {
    if (newInterviewRound.trim() && !formData.interviewRounds.includes(newInterviewRound.trim())) {
      setFormData(prev => ({ ...prev, interviewRounds: [...prev.interviewRounds, newInterviewRound.trim()] }));
      setNewInterviewRound('');
    }
  };

  const removeInterviewRound = (index) => {
    setFormData(prev => ({ ...prev, interviewRounds: prev.interviewRounds.filter((_, i) => i !== index) }));
  };

  const addScreeningQuestion = () => {
    setFormData(prev => ({
      ...prev,
      screeningQuestions: [...prev.screeningQuestions, {
        questionId: Date.now().toString(),
        questionText: '',
        questionType: 'SHORT_ANSWER',
        options: [],
        required: false
      }]
    }));
  };

  const updateScreeningQuestion = (index, field, value) => {
    setFormData(prev => {
      const updatedQuestions = [...prev.screeningQuestions];
      updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
      return { ...prev, screeningQuestions: updatedQuestions };
    });
  };

  const addQuestionOption = (questionIndex) => {
    setFormData(prev => {
      const updatedQuestions = [...prev.screeningQuestions];
      updatedQuestions[questionIndex] = {
        ...updatedQuestions[questionIndex],
        options: [...updatedQuestions[questionIndex].options, '']
      };
      return { ...prev, screeningQuestions: updatedQuestions };
    });
  };

  const updateQuestionOption = (questionIndex, optionIndex, value) => {
    setFormData(prev => {
      const updatedQuestions = [...prev.screeningQuestions];
      const updatedOptions = [...updatedQuestions[questionIndex].options];
      updatedOptions[optionIndex] = value;
      updatedQuestions[questionIndex] = { ...updatedQuestions[questionIndex], options: updatedOptions };
      return { ...prev, screeningQuestions: updatedQuestions };
    });
  };

  const removeQuestionOption = (questionIndex, optionIndex) => {
    setFormData(prev => {
      const updatedQuestions = [...prev.screeningQuestions];
      const updatedOptions = updatedQuestions[questionIndex].options.filter((_, i) => i !== optionIndex);
      updatedQuestions[questionIndex] = { ...updatedQuestions[questionIndex], options: updatedOptions };
      return { ...prev, screeningQuestions: updatedQuestions };
    });
  };

  const removeScreeningQuestion = (index) => {
    setFormData(prev => ({
      ...prev,
      screeningQuestions: prev.screeningQuestions.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.jobTitle.trim()) newErrors.jobTitle = 'Job title is required';
    if (!clientId) newErrors.client = 'Client is required';
    if (!formData.numberOfOpenings || parseInt(formData.numberOfOpenings) < 1)
      newErrors.numberOfOpenings = 'Number of openings must be at least 1';
    if (formData.experienceMax && formData.experienceMin && parseInt(formData.experienceMax) < parseInt(formData.experienceMin))
      newErrors.experienceMax = 'Max experience must be greater than or equal to min experience';
    if (formData.compensationMax && formData.compensationMin && parseFloat(formData.compensationMax) < parseFloat(formData.compensationMin))
      newErrors.compensationMax = 'Max compensation must be greater than or equal to min compensation';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      setTimeout(() => {
        const el = document.querySelector('.border-red-500');
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus?.(); }
      }, 50);
      return;
    }
    if (!organizationId) { toast.error('Organization ID not found'); return; }

    setLoading(true);
    try {
      const jobData = {
        ...formData,
        organizationId,
        clientId,
        numberOfOpenings: parseInt(formData.numberOfOpenings) || 1,
        experienceMin: formData.experienceMin ? parseInt(formData.experienceMin) : null,
        experienceMax: formData.experienceMax ? parseInt(formData.experienceMax) : null,
        compensationMin: formData.compensationMin ? parseFloat(formData.compensationMin) : null,
        compensationMax: formData.compensationMax ? parseFloat(formData.compensationMax) : null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: JSON.parse(localStorage.getItem("userProfile"))?.email || 'system',
        updatedBy: JSON.parse(localStorage.getItem("userProfile"))?.email || 'system'
      };
      const response = await jobsAPI.createJob(jobData);
      toast.success('🎉 Job created successfully!');
      onCreate(response.data);
      onClose();
    } catch (error) {
      console.error('Error creating job:', error);
      const message = error.response?.data?.message || error.message || 'Failed to create job. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? e.target.checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent";
  const inputErrorClass = (field) =>
    `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent ${errors[field] ? 'border-red-500' : 'border-gray-300'}`;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
      <div className="bg-white shadow-2xl w-full max-w-4xl h-full flex flex-col animate-slideInRight overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900">Create Job Posting</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-8">

          {/* Section 1: Basic Information */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Code</label>
                <input
                  type="text" name="jobCode" value={formData.jobCode} onChange={handleChange}
                  className={inputClass} placeholder="Auto-generated if empty"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
                <select
                  value={clientId} onChange={handleClientChange} disabled={loadingClients}
                  className={`${inputErrorClass('client')} ${loadingClients ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">{loadingClients ? 'Loading clients...' : 'Select a client'}</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>{client.companyName}</option>
                  ))}
                </select>
                {errors.client && <p className="text-sm text-red-600 mt-1">{errors.client}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Title *</label>
                <input
                  type="text" name="jobTitle" value={formData.jobTitle} onChange={handleChange}
                  className={inputErrorClass('jobTitle')} placeholder="Senior Developer"
                />
                {errors.jobTitle && <p className="text-sm text-red-600 mt-1">{errors.jobTitle}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <input
                  type="text" name="department" value={formData.department} onChange={handleChange}
                  className={inputClass} placeholder="Engineering"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Status</label>
                <select name="jobStatus" value={formData.jobStatus} onChange={handleChange}
                  className={inputClass}>
                  {jobStatusOptions.map(status => <option key={status} value={status}>{status}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Priority</label>
                <select name="jobPriority" value={formData.jobPriority} onChange={handleChange}
                  className={inputClass}>
                  {priorityOptions.map(priority => <option key={priority} value={priority}>{priority}</option>)}
                </select>
              </div>
            </div>
          </section>

          {/* Section 2: Job Details */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Job Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Model</label>
                <select name="workModel" value={formData.workModel} onChange={handleChange}
                  className={inputClass}>
                  {workModelOptions.map(model => <option key={model} value={model}>{model}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Type</label>
                <select name="jobType" value={formData.jobType} onChange={handleChange}
                  className={inputClass}>
                  {jobTypeOptions.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Openings *</label>
                <input
                  type="number" name="numberOfOpenings" value={formData.numberOfOpenings}
                  onChange={handleChange} min="1"
                  className={inputErrorClass('numberOfOpenings')}                />
                {errors.numberOfOpenings && <p className="text-sm text-red-600 mt-1">{errors.numberOfOpenings}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Location</label>
                <input
                  type="text" name="workLocation" value={formData.workLocation} onChange={handleChange}
                  className={inputClass} placeholder="e.g., New York, NY"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Working Hours</label>
                <input
                  type="text" name="workingHours" value={formData.workingHours} onChange={handleChange}
                  className={inputClass} placeholder="e.g., 9 AM - 6 PM"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Locations</label>
                <div className="flex gap-2">
                  <input
                    type="text" value={newJobLocation} onChange={(e) => setNewJobLocation(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
                    placeholder="Add job location"
                  />
                  <button type="button" onClick={addJobLocation}
                    className="px-4 py-2 text-white rounded-lg flex items-center justify-center" style={primaryBg}>
                    <Plus size={20} />
                  </button>
                </div>
                {formData.jobLocation.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.jobLocation.map((location, index) => (
                      <div key={index} className="flex items-center gap-1 px-3 py-2 bg-gray-100 rounded-lg">
                        <span className="text-sm">{location}</span>
                        <button type="button" onClick={() => removeJobLocation(index)} className="text-gray-400 hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Section 3: Skills & Experience */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Skills & Experience</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Experience (years)</label>
                <input
                  type="number" name="experienceMin" value={formData.experienceMin}
                  onChange={handleChange} min="0" className={inputClass}                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Experience (years)</label>
                <input
                  type="number" name="experienceMax" value={formData.experienceMax}
                  onChange={handleChange} min="0" className={inputErrorClass('experienceMax')}                />
                {errors.experienceMax && <p className="text-sm text-red-600 mt-1">{errors.experienceMax}</p>}
              </div>
            </div>

            {/* Skills Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Primary Skills */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Skills</label>
                <div className="flex gap-2">
                  <input
                    type="text" value={newPrimarySkill} onChange={(e) => setNewPrimarySkill(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
                    placeholder="Add primary skill"
                  />
                  <button type="button" onClick={addPrimarySkill}
                    className="px-4 py-2 text-white rounded-lg flex items-center justify-center" style={primaryBg}>
                    <Plus size={20} />
                  </button>
                </div>
                {formData.primarySkills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.primarySkills.map((skill, index) => (
                      <div key={index} className="flex items-center gap-1 px-3 py-2 rounded-lg" style={primaryLightBg}>
                        <span className="text-sm" style={primaryColor}>{skill}</span>
                        <button type="button" onClick={() => removePrimarySkill(index)}
                          className="hover:text-red-500" style={primaryColor}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Secondary Skills */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Skills</label>
                <div className="flex gap-2">
                  <input
                    type="text" value={newSecondarySkill} onChange={(e) => setNewSecondarySkill(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
                    placeholder="Add secondary skill"
                  />
                  <button type="button" onClick={addSecondarySkill}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center">
                    <Plus size={20} />
                  </button>
                </div>
                {formData.secondarySkills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.secondarySkills.map((skill, index) => (
                      <div key={index} className="flex items-center gap-1 px-3 py-2 bg-green-50 rounded-lg">
                        <span className="text-sm text-green-700">{skill}</span>
                        <button type="button" onClick={() => removeSecondarySkill(index)}
                          className="text-green-400 hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Section 4: Job Description & Management */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Job Description & Management</h3>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Job Description</label>
              <textarea
                name="jobDescription" value={formData.jobDescription} onChange={handleChange}
                rows="6" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent resize-none"
                placeholder="Enter detailed job description..."
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hiring Manager Name</label>
                <input
                  type="text" name="hiringManagerName" value={formData.hiringManagerName}
                  onChange={handleChange} className={inputClass} placeholder="Manager Name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hiring Manager Email</label>
                <input
                  type="email" name="hiringManagerEmail" value={formData.hiringManagerEmail}
                  onChange={handleChange} className={inputClass} placeholder="manager@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                <input
                  type="text" name="assignedTo" value={formData.assignedTo}
                  onChange={handleChange} className={inputClass} placeholder="Recruiter name"
                />
              </div>
            </div>
          </section>

          {/* Section 5: Compensation */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Compensation</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Compensation</label>
                <input
                  type="number" name="compensationMin" value={formData.compensationMin}
                  onChange={handleChange} min="0" step="0.01" className={inputClass}                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Compensation</label>
                <input
                  type="number" name="compensationMax" value={formData.compensationMax}
                  onChange={handleChange} min="0" step="0.01"
                  className={inputErrorClass('compensationMax')}                />
                {errors.compensationMax && <p className="text-sm text-red-600 mt-1">{errors.compensationMax}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select name="compensationCurrency" value={formData.compensationCurrency}
                  onChange={handleChange} className={inputClass}>
                  {currencyOptions.map(currency => <option key={currency} value={currency}>{currency}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pay Type</label>
                <select name="compensationPaytype" value={formData.compensationPaytype}
                  onChange={handleChange} className={inputClass}>
                  <option value="">Select pay type</option>
                  {compensationPaytypeOptions.map(paytype => <option key={paytype} value={paytype}>{paytype}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Commercials</label>
                <input
                  type="text" name="vendorCommercials" value={formData.vendorCommercials}
                  onChange={handleChange} className={inputClass}                placeholder="Vendor commercial terms"
                />
              </div>
            </div>
          </section>

          {/* Section 6: Education & Requirements */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Education & Requirements</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Education</label>
                <select name="education" value={formData.education} onChange={handleChange}
                  className={inputClass}>
                  <option value="">Select education level</option>
                  {educationOptions.map(edu => <option key={edu} value={edu}>{edu}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Institution</label>
                <input
                  type="text" name="preferredInstitution" value={formData.preferredInstitution}
                  onChange={handleChange} className={inputClass} placeholder="e.g., MIT, Stanford"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notice Period</label>
                <select name="noticePeriod" value={formData.noticePeriod} onChange={handleChange}
                  className={inputClass}>
                  <option value="">Select notice period</option>
                  {noticePeriodOptions.map(period => <option key={period} value={period}>{period}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Authorization</label>
                <select name="workAuthorization" value={formData.workAuthorization} onChange={handleChange}
                  className={inputClass}>
                  <option value="">Select work authorization</option>
                  {workAuthorizationOptions.map(auth => <option key={auth} value={auth}>{auth}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Visa Sponsorship</label>
                <select name="visaSponsorship" value={formData.visaSponsorship} onChange={handleChange}
                  className={inputClass}>
                  <option value="">Select visa sponsorship</option>
                  {visaSponsorshipOptions.map(sponsor => <option key={sponsor} value={sponsor}>{sponsor}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Background Check</label>
                <select name="backgroundCheck" value={formData.backgroundCheck} onChange={handleChange}
                  className={inputClass}>
                  <option value="">Select background check</option>
                  {backgroundCheckOptions.map(check => <option key={check} value={check}>{check}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Security Clearance</label>
                <select name="securityClearance" value={formData.securityClearance} onChange={handleChange}
                  className={inputClass}>
                  <option value="">Select security clearance</option>
                  {securityClearanceOptions.map(clearance => <option key={clearance} value={clearance}>{clearance}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">License & Certifications</label>
                <input
                  type="text" name="licenseCertifications" value={formData.licenseCertifications}
                  onChange={handleChange} className={inputClass}                  placeholder="Required licenses/certifications"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ideal Candidate Profile</label>
              <textarea
                name="idealCandidateProfile" value={formData.idealCandidateProfile}
                onChange={handleChange} rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent resize-none"
                placeholder="Describe the ideal candidate..."
              />
            </div>
          </section>

          {/* Section 7: Interview Details */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Interview Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Interview Mode</label>
                <select name="interviewMode" value={formData.interviewMode} onChange={handleChange}
                  className={inputClass}>
                  <option value="">Select interview mode</option>
                  {interviewModeOptions.map(mode => <option key={mode} value={mode}>{mode}</option>)}
                </select>
              </div>

              {/* Interview Rounds */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Interview Rounds</label>
                <div className="flex gap-2">
                  <input
                    type="text" value={newInterviewRound} onChange={(e) => setNewInterviewRound(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
                    placeholder="Add interview round"
                  />
                  <button type="button" onClick={addInterviewRound}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center">
                    <Plus size={20} />
                  </button>
                </div>
                {formData.interviewRounds.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.interviewRounds.map((round, index) => (
                      <div key={index} className="flex items-center gap-1 px-3 py-2 bg-purple-50 rounded-lg">
                        <span className="text-sm text-purple-700">{round}</span>
                        <button type="button" onClick={() => removeInterviewRound(index)}
                          className="text-purple-400 hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Section 8: Screening Questions */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Screening Questions</h3>
              <button type="button" onClick={addScreeningQuestion}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                <Plus size={16} />
                Add Question
              </button>
            </div>

            {formData.screeningQuestions.map((question, qIndex) => (
              <div key={question.questionId} className="p-4 border border-gray-200 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">Question {qIndex + 1}</h4>
                  <button type="button" onClick={() => removeScreeningQuestion(qIndex)}
                    className="p-1 text-gray-400 hover:text-red-500">
                    <Trash2 size={18} />
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Question Text</label>
                    <input
                      type="text" value={question.questionText}
                      onChange={(e) => updateScreeningQuestion(qIndex, 'questionText', e.target.value)}
                      className={inputClass} placeholder="Enter your question..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Question Type</label>
                      <select
                        value={question.questionType}
                        onChange={(e) => updateScreeningQuestion(qIndex, 'questionType', e.target.value)}
                        className={inputClass}                      >
                        <option value="SHORT_ANSWER">Short Answer</option>
                        <option value="MULTIPLE_CHOICE">Multiple Choice</option>
                      </select>
                    </div>
                    <div className="flex items-center pt-6">
                      <label className="flex items-center">
                        <input
                          type="checkbox" checked={question.required}
                          onChange={(e) => updateScreeningQuestion(qIndex, 'required', e.target.checked)}
                          className="rounded border-gray-300 mr-2"
                        />
                        <span className="text-sm text-gray-700">Required</span>
                      </label>
                    </div>
                  </div>

                  {question.questionType === 'MULTIPLE_CHOICE' && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium text-gray-700">Options</label>
                        <button type="button" onClick={() => addQuestionOption(qIndex)}
                          className="flex items-center gap-1 text-sm hover:opacity-80" style={primaryColor}>
                          <Plus size={14} />
                          Add Option
                        </button>
                      </div>
                      {question.options.map((option, oIndex) => (
                        <div key={oIndex} className="flex items-center gap-2">
                          <input
                            type="text" value={option}
                            onChange={(e) => updateQuestionOption(qIndex, oIndex, e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
                            placeholder={`Option ${oIndex + 1}`}
                          />
                          <button type="button" onClick={() => removeQuestionOption(qIndex, oIndex)}
                            className="p-2 text-gray-400 hover:text-red-500">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {formData.screeningQuestions.length === 0 && (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <FileText className="mx-auto text-gray-400 mb-2" size={24} />
                <p className="text-gray-500">No screening questions added</p>
                <p className="text-sm text-gray-400">Click "Add Question" to create screening questions</p>
              </div>
            )}
          </section>
        </form>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t bg-gray-50 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors">
            Cancel
          </button>
          <button type="submit" onClick={handleSubmit} disabled={loading}
            className="flex-1 px-6 py-3 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
            style={loading ? primaryBgHover : primaryBg}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = "#153a4f"}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = "#1C4E6D"}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white"></div>
                Creating...
              </>
            ) : (
              'Create Job Posting'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateJobModal;